// src/components/SessionTimeout.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import './SessionTimeout.css';

interface SessionTimeoutProps {
  onLogout: () => void;
  timeoutMinutes?: number;
  warningSeconds?: number;
  isAuthenticated: boolean;
  isPaused?: boolean;
}

const SessionTimeout: React.FC<SessionTimeoutProps> = ({
  onLogout,
  timeoutMinutes = 5,
  warningSeconds = 30,
  isAuthenticated,
  isPaused = false
}) => {
  const { theme } = useTheme();
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(warningSeconds);
  const isUrgent = countdown <= 10;
  
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const clearAllTimers = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  const handleLogout = useCallback(() => {
    clearAllTimers();
    setShowWarning(false);
    onLogout();
  }, [clearAllTimers, onLogout]);

  const startWarningCountdown = useCallback(() => {
    setShowWarning(true);
    setCountdown(warningSeconds);

    countdownIntervalRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          handleLogout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [warningSeconds, handleLogout]);

  const resetTimer = useCallback(() => {
    clearAllTimers();
    setShowWarning(false);
    setCountdown(warningSeconds);

    if (!isAuthenticated) return;

    // Calculate inactivity time before warning shows
    // Ensure minimum of 1 second to prevent immediate firing
    const inactivityTime = Math.max((timeoutMinutes * 60 - warningSeconds) * 1000, 1000);
    
    console.log(`Session timeout: warning will show in ${inactivityTime / 1000} seconds`);
    
    timeoutRef.current = setTimeout(() => {
      console.log('Session timeout: showing warning');
      startWarningCountdown();
    }, inactivityTime);
  }, [clearAllTimers, isAuthenticated, timeoutMinutes, warningSeconds, startWarningCountdown]);

  const handleStayLoggedIn = useCallback(() => {
    setShowWarning(false);
    resetTimer();
  }, [resetTimer]);

  // Store refs to avoid useEffect dependency issues
  const resetTimerRef = useRef(resetTimer);
  const showWarningRef = useRef(showWarning);
  resetTimerRef.current = resetTimer;
  showWarningRef.current = showWarning;

  // Handle pause state - clear timers when paused, restart when unpaused
  useEffect(() => {
    if (isPaused) {
      clearAllTimers();
      setShowWarning(false);
      console.log('Session timeout: paused (recording in progress)');
    } else if (isAuthenticated && !isPaused) {
      // Restart timer when unpaused
      console.log('Session timeout: resumed');
      resetTimerRef.current();
    }
  }, [isPaused, isAuthenticated, clearAllTimers]);

  useEffect(() => {
    if (!isAuthenticated || isPaused) {
      clearAllTimers();
      setShowWarning(false);
      return;
    }

    const events = [
      'mousedown',
      'keydown',
      'scroll',
      'touchstart',
      'keypress',
      'input',
      'change'
    ];

    const handleActivity = (event: Event) => {
      // Use ref to get current value without causing re-renders
      if (showWarningRef.current) return;
      
      // Only reset timer for interactions within the app (not browser chrome)
      const target = event.target;
      if (!target) return;
      
      // Check if target is an Element (has closest method) vs Document
      if (!(target instanceof Element)) {
        // For non-element targets (like document), just reset the timer
        resetTimerRef.current();
        return;
      }
      
      // Check if the event target is within the app root or a modal/overlay
      const appRoot = document.querySelector('.app-root');
      const isInApp = appRoot?.contains(target);
      const isInModal = target.closest('.session-timeout-overlay') || 
                        target.closest('.modal') || 
                        target.closest('[role="dialog"]');
      
      if (isInApp || isInModal) {
        resetTimerRef.current();
      }
    };

    events.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Initial timer start - only on mount
    resetTimerRef.current();

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity as EventListener);
      });
      clearAllTimers();
    };
  }, [isAuthenticated, clearAllTimers]);

  if (!showWarning || !isAuthenticated) {
    return null;
  }

  return (
    <div className={`session-timeout-overlay ${theme}`}>
      <div className="session-timeout-modal">
        <div className={`timeout-icon ${isUrgent ? 'urgent' : ''}`}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12 6 12 12 16 14"/>
          </svg>
        </div>
        <p>You will be logged out due to inactivity in</p>
        <div className={`countdown-display ${isUrgent ? 'urgent' : ''}`}>
          <span className="countdown-number">{countdown}</span>
          <span className="countdown-label">seconds</span>
        </div>
        <button className="stay-logged-in-btn" onClick={handleStayLoggedIn}>
          Stay Logged In
        </button>
      </div>
    </div>
  );
};

export default SessionTimeout;
